/**
 * Shared data-freshness context and polling hook.
 *
 * Provides a React context that polls GET /api/freshness every 5 minutes
 * with a `since` parameter to detect new data.  Consumers get:
 *
 *   - `freshness`        {Object}  — domain_id -> status map (for staleness dots)
 *   - `domains`          {Array}   — raw domain freshness objects from the API
 *   - `newDataAvailable` {boolean} — true when the backend detects data newer than baseline
 *   - `checkedAt`        {string}  — ISO timestamp of the last successful poll
 *   - `dismiss`          {Function} — hide the banner and advance the baseline
 *
 * The baseline is established on the first successful poll (no `since` sent,
 * so the backend returns `new_data_available = false`).  Subsequent polls
 * send `since = baseline` so the backend can report whether new data arrived
 * after that point.  Dismissing the banner advances the baseline to the
 * latest `checked_at`, preventing the same data from re-triggering the
 * banner.  A full page reload naturally resets everything.
 *
 * Spec reference: section 6.14 (Frontend: New Data Banner).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getFreshness } from '../utils/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Poll interval: 5 minutes. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FreshnessContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Wrap authenticated routes with this provider to enable freshness polling.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function FreshnessProvider({ children }) {
  const [domains, setDomains] = useState([]);
  const [freshness, setFreshness] = useState({});
  const [newDataAvailable, setNewDataAvailable] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  // Generation time of the newest our-price feed snapshot the dashboard
  // holds (UTC ISO), or null before the first snapshot.
  const [ourPricesFeedAsOf, setOurPricesFeedAsOf] = useState(null);

  // Baseline timestamp: passed as `since` to detect *new* data.
  // null on first poll (backend returns new_data_available = false).
  const baselineRef = useRef(null);

  // Mirror of checkedAt in a ref so dismiss() never has a stale closure.
  const checkedAtRef = useRef(null);

  // Guards against overlapping polls: if a request is still in flight when
  // the interval fires (slow/stalled network), skip this tick rather than
  // stacking a second concurrent request.
  const inFlightRef = useRef(false);

  // ------ Poll function ------
  const poll = useCallback(async () => {
    if (inFlightRef.current) {
      return; // previous poll still running — skip this tick
    }
    inFlightRef.current = true;
    try {
      const params = {};
      if (baselineRef.current) {
        params.since = baselineRef.current;
      }

      const result = await getFreshness(params);

      // Build domain_id -> status object map
      const map = {};
      for (const d of result.domains) {
        map[d.domain_id] = d;
      }

      setDomains(result.domains);
      setFreshness(map);
      setCheckedAt(result.checked_at);
      checkedAtRef.current = result.checked_at;
      setOurPricesFeedAsOf(result.our_prices_feed_as_of ?? null);

      // Establish baseline on first successful poll
      if (!baselineRef.current) {
        baselineRef.current = result.checked_at;
      }

      // Show banner when new data is detected
      if (result.new_data_available) {
        setNewDataAvailable(true);
      }
    } catch (err) {
      // The banner is advisory and must never block normal usage, so we
      // don't surface a blocking error — but log it so repeated freshness
      // failures are visible to operators in the browser console.
      console.warn('Data-freshness poll failed:', err);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // ------ Start polling on mount ------
  useEffect(() => {
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [poll]);

  // ------ Dismiss handler ------
  const dismiss = useCallback(() => {
    setNewDataAvailable(false);
    // Advance baseline so the *same* data won't re-trigger the banner.
    // Only truly newer data (arriving after this point) will show it again.
    if (checkedAtRef.current) {
      baselineRef.current = checkedAtRef.current;
    }
  }, []);

  // ------ Context value ------
  const value = {
    domains,
    freshness,
    newDataAvailable,
    checkedAt,
    ourPricesFeedAsOf,
    setOurPricesFeedAsOf,
    dismiss,
  };

  return (
    <FreshnessContext.Provider value={value}>
      {children}
    </FreshnessContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the shared data-freshness context.
 *
 * Must be called inside a `<FreshnessProvider>`.
 *
 * @returns {{
 *   domains: Array,
 *   freshness: Object,
 *   newDataAvailable: boolean,
 *   checkedAt: string|null,
 *   ourPricesFeedAsOf: string|null,
 *   setOurPricesFeedAsOf: Function,
 *   dismiss: Function,
 * }}
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDataFreshness() {
  const ctx = useContext(FreshnessContext);
  if (!ctx) {
    throw new Error('useDataFreshness must be used inside <FreshnessProvider>');
  }
  return ctx;
}
